import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import './WarehouseDashboard.css';
import { formatAppDate, parseApiDate, localToday } from '../../utils/dateUtils';
import { showApiError } from '../../utils/errorUtils';
import { defaultLocationForItemType, preferredLocation } from '../../utils/locations';
import GRNInspectionModal from '../../components/Quality/GRNInspectionModal';
import { hasRole } from '../../utils/roleUtils';

const ReceivingOperations = forwardRef(({ activeSubTab, onCreateReturn }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();
  const statusLabel = (code) => (code ? t(`receivingOperations.status.${code}`, { defaultValue: code }) : code);

  // Incoming deliveries state
  const [incomingDeliveries, setIncomingDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poDetails, setPODetails] = useState(null);
  const [loadingPODetails, setLoadingPODetails] = useState(false);

  // Receive / GRN state
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(localToday());
  const [grnNotes, setGrnNotes] = useState('');
  const [submittingGRN, setSubmittingGRN] = useState(false);
  const [grns, setGrns] = useState([]);
  const [loadingGRNs, setLoadingGRNs] = useState(false);

  // Quality inspection state
  const [qualityItems, setQualityItems] = useState([]);
  const [loadingQualityItems, setLoadingQualityItems] = useState(false);
  const [selectedQualityItem, setSelectedQualityItem] = useState(null);

  // GRN details modal state
  const [showGRNDetailsModal, setShowGRNDetailsModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [grnDetails, setGrnDetails] = useState(null);
  const [loadingGRNDetails, setLoadingGRNDetails] = useState(false);

  // Cancel GRN (#281): SUPERVISOR, MANAGER and ADMIN only
  const [showCancelGRNModal, setShowCancelGRNModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingGRN, setCancellingGRN] = useState(false);
  const canCancelGRN = hasRole(user, 'ADMIN', 'SUPERVISOR', 'MANAGER');

  // Warehouse locations (needed for receive modal)
  const [warehouseLocations, setWarehouseLocations] = useState([]);

  // ---------------------------------------------------------------------------
  // Expose refresh() via ref
  // ---------------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (activeSubTab === 'incoming') {
        fetchIncomingDeliveries();
      } else if (activeSubTab === 'receipt') {
        fetchGRNs();
      } else if (activeSubTab === 'quality') {
        fetchQualityItems();
      }
    }
  }));

  // ---------------------------------------------------------------------------
  // Fetch on mount & when activeSubTab changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchWarehouseLocations();
    fetchIncomingDeliveries();
    fetchGRNs();
    fetchQualityItems();
  }, []);

  useEffect(() => {
    if (activeSubTab === 'incoming') {
      fetchIncomingDeliveries();
    } else if (activeSubTab === 'receipt') {
      fetchGRNs();
    } else if (activeSubTab === 'quality') {
      fetchQualityItems();
    }
  }, [activeSubTab]);

  // ---------------------------------------------------------------------------
  // Fetch functions
  // ---------------------------------------------------------------------------
  const fetchIncomingDeliveries = async () => {
    try {
      setLoadingDeliveries(true);
      // Fetch purchase orders with status SENT, CONFIRMED, or PARTIALLY_RECEIVED
      const response = await apiFetch(getApiUrl('/api/purchase-orders?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        // Filter for incoming deliveries (not yet fully received)
        const incoming = (data || []).filter(po =>
          ['SENT', 'CONFIRMED', 'PARTIALLY_RECEIVED'].includes(po.status)
        );
        // Sort by expected delivery date
        incoming.sort((a, b) => new Date(a.expected_delivery) - new Date(b.expected_delivery));
        setIncomingDeliveries(incoming);
      }
    } catch (error) {
      console.error('Error fetching incoming deliveries:', error);
      setIncomingDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const fetchGRNs = async () => {
    try {
      setLoadingGRNs(true);
      const response = await apiFetch(getApiUrl('/api/grns?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setGrns(data || []);
      }
    } catch (error) {
      console.error('Error fetching GRNs:', error);
      setGrns([]);
    } finally {
      setLoadingGRNs(false);
    }
  };

  const fetchQualityItems = async () => {
    try {
      setLoadingQualityItems(true);
      const response = await apiFetch(getApiUrl('/api/grn-items/quality-inspection?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setQualityItems(data || []);
      }
    } catch (error) {
      console.error('Error fetching quality items:', error);
      setQualityItems([]);
    } finally {
      setLoadingQualityItems(false);
    }
  };

  const fetchWarehouseLocations = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=200'));
      if (response.ok) {
        const data = await response.json();
        setWarehouseLocations(data || []);
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      setWarehouseLocations([]);
    }
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleViewPO = async (po) => {
    setSelectedPO(po);
    setShowPOModal(true);

    // Fetch detailed PO information including line items
    try {
      setLoadingPODetails(true);
      const poId = po.purchase_order_id || po.po_id;
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${poId}`));
      if (response.ok) {
        const data = await response.json();
        setPODetails(data);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setPODetails(null);
    } finally {
      setLoadingPODetails(false);
    }
  };

  const closePOModal = () => {
    setShowPOModal(false);
    setSelectedPO(null);
    setPODetails(null);
  };

  const handleReceiveGoods = () => {
    // Initialize receive items from PO details
    if (poDetails && poDetails.lines) {
      const activeLocations = warehouseLocations.filter(loc => loc.is_active).map(loc => loc.location_code);
      const items = poDetails.lines.map(line => {
        const ordered = parseFloat(line.quantity || line.quantity_ordered || 0);
        const received = parseFloat(line.quantity_received || line.received_quantity || 0);
        const outstanding = Math.max(0, ordered - received);
        return {
          item_id: line.line_id || line.item_id,
          item_code: line.product_code || line.item_code,
          item_name: line.product_name || line.item_name,
          quantity_ordered: ordered,
          quantity_received: received,
          quantity_to_receive: outstanding, // Default to outstanding quantity
          // The location shown in the select is the one sent: the item's default location, else the one
          // for its type (INK -> BT-001, ...), else the first one
          location: activeLocations.includes(line.default_location)
            ? line.default_location
            : preferredLocation(activeLocations, defaultLocationForItemType(line.item_type)),
          quality_status: 'PASSED',
          batch_number: '',
          supplier_batch_number: '',
          notes: ''
        };
      });
      setReceiveItems(items);
      setShowReceiveModal(true);
      setShowPOModal(false); // Close PO modal when opening receive modal
    }
  };

  const closeReceiveModal = () => {
    setShowReceiveModal(false);
    setReceiveItems([]);
    setDeliveryDate(localToday());
    setGrnNotes('');
  };

  // QC decision (accept / quarantine / reject) in the shared modal, also used by Quality Control → Receipts
  const handleInspectItem = (item) => {
    setSelectedQualityItem(item);
  };

  const handleInspectionSaved = () => {
    fetchQualityItems();
    if (showGRNDetailsModal && selectedGRN) {
      // Refresh GRN details if modal is open
      handleViewGRN(selectedGRN);
    }
  };

  const handleViewGRN = async (grn) => {
    setSelectedGRN(grn);
    setShowGRNDetailsModal(true);

    try {
      setLoadingGRNDetails(true);
      const response = await apiFetch(getApiUrl(`/api/grns/${grn.grn_id}`));
      if (response.ok) {
        const data = await response.json();
        setGrnDetails(data);
      }
    } catch (error) {
      console.error('Error fetching GRN details:', error);
      setGrnDetails(null);
    } finally {
      setLoadingGRNDetails(false);
    }
  };

  const closeGRNDetailsModal = () => {
    setShowGRNDetailsModal(false);
    setSelectedGRN(null);
    setGrnDetails(null);
  };

  const closeCancelGRNModal = () => {
    setShowCancelGRNModal(false);
    setCancelReason('');
  };

  const handleCancelGRN = async () => {
    if (!grnDetails || cancellingGRN || cancelReason.trim().length < 5) return;
    setCancellingGRN(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/grns/${grnDetails.grn_id}/cancel`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      if (response.ok) {
        const result = await response.json();
        showSuccess(t('receivingOperations.toasts.grnCancelled'), t('receivingOperations.toasts.grnCancelledMessage', { grnNumber: result.grn_number, poNumber: result.po_number, poStatus: statusLabel(result.po_status) }));
        closeCancelGRNModal();
        handleViewGRN(selectedGRN);
        fetchGRNs();
        fetchQualityItems();
      } else {
        await showApiError(response, showError, tCommon, t('receivingOperations.toasts.failedToCancelGrnTitle'));
      }
    } catch (error) {
      console.error('Error cancelling GRN:', error);
      showError(t('errors.generic.error'), t('receivingOperations.toasts.failedToCancelGrn'));
    } finally {
      setCancellingGRN(false);
    }
  };

  const handleInspectFromGRN = (item) => {
    // Use the existing inspection modal
    handleInspectItem(item);
  };

  const updateReceiveItem = (index, field, value) => {
    const updated = [...receiveItems];
    updated[index][field] = value;
    setReceiveItems(updated);
  };

  const submitGRN = async () => {
    try {
      setSubmittingGRN(true);

      // Filter items with quantity_to_receive > 0
      const itemsToReceive = receiveItems
        .filter(item => item.quantity_to_receive > 0)
        .map(item => ({
          item_id: item.item_id,
          quantity_received: parseFloat(item.quantity_to_receive),
          location: item.location,
          quality_status: item.quality_status,
          batch_number: item.batch_number || null,
          supplier_batch_number: item.supplier_batch_number || null,
          notes: item.notes || null
        }));

      if (itemsToReceive.length === 0) {
        showWarning(tCommon('ui.invalidInput'), t('receivingOperations.toasts.enterQuantities'));
        return;
      }

      // Validate quantities don't exceed outstanding
      for (const item of receiveItems) {
        if (item.quantity_to_receive > 0) {
          const outstanding = Math.max(0, item.quantity_ordered - item.quantity_received);
          if (item.quantity_to_receive > outstanding) {
            showError(t('receivingOperations.toasts.invalidQuantity'), t('receivingOperations.toasts.exceedsOutstanding', { quantity: item.quantity_to_receive, item: item.item_name, outstanding }));
            return;
          }
        }
      }

      const poId = selectedPO.purchase_order_id || selectedPO.po_id;
      const response = await apiFetch(getApiUrl(`/api/purchase-orders/${poId}/receive`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actual_delivery_date: deliveryDate + 'T12:00:00',
          grn_notes: grnNotes,
          items: itemsToReceive,
          received_by: user.user_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.goodsReceipt.grnCreated'), t('success.goodsReceipt.grnCreatedMessage', { grnNumber: result.grn_number, itemsReceived: result.items_received, transactionsCreated: result.transactions_created, newStatus: statusLabel(result.new_status) }));

        // Refresh data
        fetchIncomingDeliveries();
        fetchGRNs();
        closeReceiveModal();
      } else {
        await showApiError(response, showError, tCommon, t('errors.goodsReceipt.failedToReceiveGoods'));
      }
    } catch (error) {
      console.error('Error submitting GRN:', error);
      showError(t('errors.generic.error'), t('errors.goodsReceipt.failedToSubmitReceipt'));
    } finally {
      setSubmittingGRN(false);
    }
  };

  const handleCreateReturn = (grnItem) => {
    if (onCreateReturn) {
      // Lines inside GRN Details carry no GRN/PO/supplier of their own
      const grn = showGRNDetailsModal && grnDetails ? grnDetails : {};
      onCreateReturn({
        grn_number: grn.grn_number, po_number: grn.po_number, supplier_name: grn.supplier_name,
        ...grnItem
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Render functions
  // ---------------------------------------------------------------------------
  const renderIncomingDeliveries = () => (
    <div className="warehouse-section">
      {loadingDeliveries ? (
        <div className="loading-state">{t('receivingOperations.loading.incomingDeliveries')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('receivingOperations.columns.poNumber')}</th>
                <th>{t('receivingOperations.columns.supplier')}</th>
                <th>{t('receivingOperations.columns.orderDate')}</th>
                <th>{t('receivingOperations.columns.expectedDelivery')}</th>
                <th>{t('receivingOperations.columns.totalAmount')}</th>
                <th>{tCommon('ui.status')}</th>
                <th>{tCommon('ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {incomingDeliveries.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('receivingOperations.empty.noIncomingDeliveries')}
                  </td>
                </tr>
              ) : (
                incomingDeliveries.map(po => {
                  const expectedDate = po.expected_delivery || po.expected_delivery_date;
                  const daysUntilDelivery = expectedDate
                    ? Math.ceil((parseApiDate(expectedDate) - new Date()) / (1000 * 60 * 60 * 24))
                    : null;

                  let statusClass = 'status-expected';
                  if (po.status === 'CONFIRMED') statusClass = 'status-in-transit';
                  if (po.status === 'PARTIALLY_RECEIVED') statusClass = 'status-pending-verification';

                  return (
                    <tr key={po.purchase_order_id || po.po_id}>
                      <td><strong>{po.po_number}</strong></td>
                      <td>{po.supplier_name || po.provider_name || t('receivingOperations.labels.unknownSupplier')}</td>
                      <td>{po.order_date ? formatAppDate(po.order_date) : '-'}</td>
                      <td>
                        {(po.expected_delivery || po.expected_delivery_date) ? (
                          <>
                            {formatAppDate(po.expected_delivery || po.expected_delivery_date)}
                            {daysUntilDelivery !== null && (
                              <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.85em',
                                color: daysUntilDelivery < 0 ? 'var(--error)' : daysUntilDelivery < 7 ? 'var(--warning)' : 'var(--text-secondary)'
                              }}>
                                ({daysUntilDelivery < 0 ? t('receivingOperations.labels.overdue') : daysUntilDelivery === 0 ? tCommon('ui.today') : t('receivingOperations.labels.days', { count: daysUntilDelivery })})
                              </span>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td>{po.currency} {po.total_amount ? parseFloat(po.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'}</td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(po.status)}</span></td>
                      <td>
                        <button className="btn-action btn-secondary" onClick={() => handleViewPO(po)}>{t('receivingOperations.buttons.viewPO')}</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderGoodsReceipt = () => (
    <div className="warehouse-section">
      {loadingGRNs ? (
        <div className="loading-state">{t('receivingOperations.loading.grns')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('receivingOperations.columns.grnNumber')}</th>
                <th>{t('receivingOperations.columns.poReference')}</th>
                <th>{t('receivingOperations.columns.supplier')}</th>
                <th>{t('receivingOperations.columns.receiptDate')}</th>
                <th>{t('receivingOperations.columns.receivedBy')}</th>
                <th>{t('receivingOperations.columns.itemsQty')}</th>
                <th>{tCommon('ui.status')}</th>
                <th>{tCommon('ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {grns.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('receivingOperations.empty.noGrns')}
                  </td>
                </tr>
              ) : (
                grns.map(grn => {
                  let statusClass = 'status-pending';
                  if (grn.status === 'VERIFIED') statusClass = 'status-verified';
                  if (grn.status === 'SUBMITTED') statusClass = 'status-in-transit';
                  if (grn.status === 'CANCELLED') statusClass = 'status-out-of-stock';

                  return (
                    <tr key={grn.grn_id}>
                      <td><strong>{grn.grn_number}</strong></td>
                      <td>{grn.po_number}</td>
                      <td>{grn.supplier_name || tCommon('ui.unknown')}</td>
                      <td>{grn.receipt_date ? formatAppDate(grn.receipt_date) : '-'}</td>
                      <td>{grn.received_by_name || t('receivingOperations.labels.userNumber', { id: grn.received_by })}</td>
                      <td>
                        {t('receivingOperations.labels.itemsCount', { count: grn.items_count || 0 })}
                        {grn.total_quantity && <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                          ({t('receivingOperations.labels.unitsCount', { quantity: parseFloat(grn.total_quantity).toLocaleString() })})
                        </span>}
                      </td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(grn.status)}</span></td>
                      <td>
                        <button className="btn-action btn-secondary" onClick={() => handleViewGRN(grn)}>
                          {t('receivingOperations.buttons.viewDetails')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderQualityCheck = () => (
    <div className="warehouse-section">
      {loadingQualityItems ? (
        <div className="loading-state">{t('receivingOperations.loading.qualityItems')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('receivingOperations.columns.grnReference')}</th>
                <th>{t('receivingOperations.columns.poNumber')}</th>
                <th>{tCommon('ui.material')}</th>
                <th>{tCommon('ui.description')}</th>
                <th>{tCommon('ui.quantity')}</th>
                <th>{t('receivingOperations.columns.batchNumber')}</th>
                <th>{t('receivingOperations.columns.receiptDate')}</th>
                <th>{tCommon('ui.status')}</th>
                <th>{tCommon('ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {qualityItems.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('receivingOperations.empty.noQualityItems')}
                    <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                      {t('receivingOperations.empty.noQualityItemsHint')}
                    </p>
                  </td>
                </tr>
              ) : (
                qualityItems.map(item => {
                  let statusClass = 'status-pending';
                  if (item.quality_status === 'QUARANTINE') statusClass = 'status-warning';
                  if (item.quality_status === 'FAILED') statusClass = 'status-error';

                  return (
                    <tr key={item.grn_item_id}>
                      <td><strong>{item.grn_number}</strong></td>
                      <td>{item.po_number}</td>
                      <td><strong>{item.item_code}</strong></td>
                      <td>{item.item_name}</td>
                      <td>{parseFloat(item.quantity_received).toLocaleString()} {item.unit_of_measure}</td>
                      <td>{item.batch_number || '-'}</td>
                      <td>{item.receipt_date ? formatAppDate(item.receipt_date) : '-'}</td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(item.quality_status)}</span></td>
                      <td>
                        <button className="btn-action btn-primary" onClick={() => handleInspectItem(item)}>
                          {t('receivingOperations.buttons.inspect')}
                        </button>
                        {item.quality_status === 'FAILED' && (
                          <button className="btn-action btn-danger" onClick={() => handleCreateReturn(item)} style={{ marginLeft: '0.5rem' }}>
                            {t('receivingOperations.buttons.createReturn')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------
  const renderContent = () => {
    switch (activeSubTab) {
      case 'incoming':
        return renderIncomingDeliveries();
      case 'receipt':
        return renderGoodsReceipt();
      case 'quality':
        return renderQualityCheck();
      default:
        return renderIncomingDeliveries();
    }
  };

  return (
    <>
      {renderContent()}

      {/* Purchase Order Details Modal */}
      {showPOModal && (
        <div className="modal-overlay" onClick={closePOModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('receivingOperations.poModal.title')}</h2>
              <button className="modal-close" onClick={closePOModal}>&#x2715;</button>
            </div>

            {selectedPO && (
              <div className="modal-material-info">
                <div className="material-info-row">
                  <span className="info-label">{t('receivingOperations.columns.poNumber')}:</span>
                  <span className="info-value"><strong>{selectedPO.po_number}</strong></span>
                  <span className="info-label">{tCommon('ui.status')}:</span>
                  <span className="info-value">
                    <span className={`status-badge status-${
                      selectedPO.status === 'CONFIRMED' ? 'in-transit' :
                      selectedPO.status === 'PARTIALLY_RECEIVED' ? 'pending-verification' :
                      'expected'
                    }`}>
                      {statusLabel(selectedPO.status)}
                    </span>
                  </span>
                </div>
                <div className="material-info-row">
                  <span className="info-label">{t('receivingOperations.columns.supplier')}:</span>
                  <span className="info-value">{selectedPO.supplier_name || selectedPO.provider_name || tCommon('ui.unknown')}</span>
                  <span className="info-label">{t('receivingOperations.columns.orderDate')}:</span>
                  <span className="info-value">{selectedPO.order_date ? formatAppDate(selectedPO.order_date) : '-'}</span>
                </div>
                <div className="material-info-row">
                  <span className="info-label">{t('receivingOperations.columns.expectedDelivery')}:</span>
                  <span className="info-value">
                    {(selectedPO.expected_delivery || selectedPO.expected_delivery_date) ?
                      formatAppDate(selectedPO.expected_delivery || selectedPO.expected_delivery_date) : '-'}
                  </span>
                  <span className="info-label">{t('receivingOperations.columns.totalAmount')}:</span>
                  <span className="info-value">
                    <strong>{selectedPO.currency} {selectedPO.total_amount ? parseFloat(selectedPO.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'}</strong>
                  </span>
                </div>
                {selectedPO.notes && (
                  <div className="material-info-row">
                    <span className="info-label">{tCommon('ui.notes')}:</span>
                    <span className="info-value">{selectedPO.notes}</span>
                  </div>
                )}
              </div>
            )}

            <div className="modal-body">
              {loadingPODetails ? (
                <div className="loading-state">{t('receivingOperations.loading.poDetails')}</div>
              ) : !poDetails ? (
                <div className="no-data">
                  <p>{t('receivingOperations.empty.poDetailsUnavailable')}</p>
                </div>
              ) : (
                <div>
                  {poDetails.lines && poDetails.lines.length > 0 && (
                    <div className="transactions-table-container">
                      <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>{t('receivingOperations.poModal.orderItems')}</h3>
                      <table className="warehouse-table">
                        <thead>
                          <tr>
                            <th>{tCommon('ui.material')}</th>
                            <th>{tCommon('ui.description')}</th>
                            <th>{tCommon('ui.quantity')}</th>
                            <th>{tCommon('ui.unitPrice')}</th>
                            <th>{tCommon('ui.total')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poDetails.lines.map((line, index) => (
                            <tr key={line.line_id || index}>
                              <td><strong>{line.product_code || '-'}</strong></td>
                              <td>{line.product_name || '-'}</td>
                              <td>{line.quantity} {line.unit_of_measure || 'pcs'}</td>
                              <td>{selectedPO.currency} {parseFloat(line.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td><strong>{selectedPO.currency} {parseFloat(line.total_price || line.line_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closePOModal}>{tCommon('ui.close')}</button>
              {selectedPO && ['SENT', 'CONFIRMED', 'PARTIALLY_RECEIVED'].includes(selectedPO.status) && (
                <button className="btn-action btn-success" onClick={handleReceiveGoods}>{t('receivingOperations.buttons.receiveGoods')}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receive Goods / GRN Modal */}
      {showReceiveModal && (
        <div className="modal-overlay" onClick={closeReceiveModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('receivingOperations.receiveModal.title', { poNumber: selectedPO?.po_number })}</h2>
              <button className="modal-close" onClick={closeReceiveModal}>&#x2715;</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('receivingOperations.columns.supplier')}:</span>
                <span className="info-value">{selectedPO?.supplier_name || selectedPO?.provider_name}</span>
                <span className="info-label">{t('receivingOperations.receiveModal.deliveryDate')}:</span>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="receive-date-input"
                />
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('receivingOperations.labels.grnNotes')}:</span>
                <input
                  type="text"
                  value={grnNotes}
                  onChange={(e) => setGrnNotes(e.target.value)}
                  placeholder={t('placeholders.deliveryNotes')}
                  className="receive-notes-input"
                />
              </div>
            </div>

            <div className="modal-body">
              <div className="transactions-table-container">
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{tCommon('ui.material')}</th>
                      <th>{tCommon('ui.description')}</th>
                      <th>{t('receivingOperations.columns.ordered')}</th>
                      <th>{t('receivingOperations.columns.previouslyReceived')}</th>
                      <th>{t('receivingOperations.columns.outstanding')}</th>
                      <th>{t('receivingOperations.columns.receiveNow')}</th>
                      <th>{t('receivingOperations.columns.location')}</th>
                      <th>{t('receivingOperations.columns.quality')}</th>
                      <th>{t('receivingOperations.columns.batchNumber')}</th>
                      <th>{t('labels.supplierBatchNumber', 'Supplier Batch #')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiveItems.map((item, index) => {
                      const outstanding = item.quantity_ordered - item.quantity_received;
                      return (
                        <tr key={item.item_id}>
                          <td><strong>{item.item_code}</strong></td>
                          <td>{item.item_name}</td>
                          <td>{item.quantity_ordered}</td>
                          <td>{item.quantity_received}</td>
                          <td style={{ fontWeight: 600, color: outstanding > 0 ? 'var(--warning)' : 'var(--success)' }}>
                            {outstanding}
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={outstanding}
                              step="0.001"
                              value={item.quantity_to_receive}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateReceiveItem(index, 'quantity_to_receive', Math.min(val, outstanding));
                              }}
                              disabled={outstanding <= 0}
                              className="receive-quantity-input"
                              style={outstanding <= 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            />
                          </td>
                          <td>
                            <select
                              value={item.location}
                              onChange={(e) => updateReceiveItem(index, 'location', e.target.value)}
                              className="receive-location-select"
                            >
                              {warehouseLocations
                                .filter(loc => loc.is_active)
                                .map(loc => (
                                  <option key={loc.location_id} value={loc.location_code}>
                                    {loc.location_code} - {loc.location_name}
                                  </option>
                                ))}
                            </select>
                          </td>
                          <td>
                            <select
                              value={item.quality_status}
                              onChange={(e) => updateReceiveItem(index, 'quality_status', e.target.value)}
                              className="receive-quality-select"
                            >
                              <option value="PASSED">{statusLabel('PASSED')}</option>
                              <option value="QUARANTINE">{statusLabel('QUARANTINE')}</option>
                              <option value="FAILED">{statusLabel('FAILED')}</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              value={item.batch_number}
                              onChange={(e) => updateReceiveItem(index, 'batch_number', e.target.value)}
                              placeholder={t('placeholders.batchNumber')}
                              className="receive-batch-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={item.supplier_batch_number}
                              onChange={(e) => updateReceiveItem(index, 'supplier_batch_number', e.target.value)}
                              placeholder={t('placeholders.supplierBatchNumber', 'Supplier batch #')}
                              className="receive-batch-input"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeReceiveModal} disabled={submittingGRN}>
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitGRN}
                disabled={submittingGRN || receiveItems.every(item => item.quantity_to_receive === 0)}
              >
                {submittingGRN ? t('receivingOperations.buttons.submitting') : t('receivingOperations.buttons.submitGrn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Inspection Modal */}
      {selectedQualityItem && (
        <GRNInspectionModal
          item={selectedQualityItem}
          onClose={() => setSelectedQualityItem(null)}
          onSaved={handleInspectionSaved}
        />
      )}

      {/* GRN Details Modal */}
      {showGRNDetailsModal && selectedGRN && (
        <div className="modal-overlay" onClick={closeGRNDetailsModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1400px' }}>
            <div className="modal-header">
              <h2>{t('receivingOperations.grnModal.title', { grnNumber: selectedGRN.grn_number })}</h2>
              <button className="modal-close" onClick={closeGRNDetailsModal}>&#x2715;</button>
            </div>

            {loadingGRNDetails ? (
              <div className="modal-body">
                <div className="loading-state">{t('receivingOperations.loading.grnDetails')}</div>
              </div>
            ) : !grnDetails ? (
              <div className="modal-body">
                <div className="no-data">{t('receivingOperations.empty.grnDetailsFailed')}</div>
              </div>
            ) : (
              <>
                <div className="modal-material-info">
                  <div className="material-info-row">
                    <span className="info-label">{t('receivingOperations.columns.grnNumber')}:</span>
                    <span className="info-value"><strong>{grnDetails.grn_number}</strong></span>
                    <span className="info-label">{tCommon('ui.status')}:</span>
                    <span className="info-value">
                      <span className={`status-badge ${
                        grnDetails.status === 'VERIFIED' ? 'status-verified' :
                        grnDetails.status === 'SUBMITTED' ? 'status-in-transit' :
                        grnDetails.status === 'CANCELLED' ? 'status-out-of-stock' : 'status-pending'
                      }`}>
                        {statusLabel(grnDetails.status)}
                      </span>
                    </span>
                  </div>
                  <div className="material-info-row">
                    <span className="info-label">{t('receivingOperations.columns.poNumber')}:</span>
                    <span className="info-value">{grnDetails.po_number}</span>
                    <span className="info-label">{t('receivingOperations.columns.supplier')}:</span>
                    <span className="info-value">{grnDetails.supplier_name || tCommon('ui.unknown')}</span>
                  </div>
                  <div className="material-info-row">
                    <span className="info-label">{t('receivingOperations.columns.receiptDate')}:</span>
                    <span className="info-value">
                      {grnDetails.receipt_date ? formatAppDate(grnDetails.receipt_date, 'datetime') : '-'}
                    </span>
                    <span className="info-label">{t('receivingOperations.columns.receivedBy')}:</span>
                    <span className="info-value">{grnDetails.received_by_name || t('receivingOperations.labels.userHash', { id: grnDetails.received_by })}</span>
                  </div>
                  {grnDetails.supplier_delivery_note && (
                    <div className="material-info-row">
                      <span className="info-label">{t('receivingOperations.grnModal.deliveryNote')}:</span>
                      <span className="info-value">{grnDetails.supplier_delivery_note}</span>
                      <span className="info-label">{t('receivingOperations.grnModal.vehicle')}:</span>
                      <span className="info-value">{grnDetails.vehicle_number || '-'}</span>
                    </div>
                  )}
                  {grnDetails.verified_by && (
                    <div className="material-info-row">
                      <span className="info-label">{t('receivingOperations.grnModal.verifiedBy')}:</span>
                      <span className="info-value">{grnDetails.verified_by_name || t('receivingOperations.labels.userHash', { id: grnDetails.verified_by })}</span>
                      <span className="info-label">{t('receivingOperations.grnModal.verifiedDate')}:</span>
                      <span className="info-value">
                        {grnDetails.verified_date ? formatAppDate(grnDetails.verified_date, 'datetime') : '-'}
                      </span>
                    </div>
                  )}
                  {grnDetails.status === 'CANCELLED' && (
                    <div className="material-info-row">
                      <span className="info-label">{t('receivingOperations.grnModal.cancelledBy')}:</span>
                      <span className="info-value">
                        {grnDetails.cancelled_by_name || (grnDetails.cancelled_by ? t('receivingOperations.labels.userHash', { id: grnDetails.cancelled_by }) : '-')}
                        {grnDetails.cancelled_date ? ` · ${formatAppDate(grnDetails.cancelled_date, 'datetime')}` : ''}
                      </span>
                      <span className="info-label">{t('receivingOperations.grnModal.reason')}:</span>
                      <span className="info-value">{grnDetails.cancellation_reason || '-'}</span>
                    </div>
                  )}
                  {grnDetails.notes && (
                    <div className="material-info-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span className="info-label">{t('receivingOperations.labels.grnNotes')}:</span>
                      <span className="info-value" style={{ marginTop: '0.5rem' }}>{grnDetails.notes}</span>
                    </div>
                  )}
                </div>

                <div className="modal-body">
                  <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>{t('receivingOperations.grnModal.receivedItems')}</h3>
                  <div className="transactions-table-container">
                    <table className="warehouse-table">
                      <thead>
                        <tr>
                          <th>{tCommon('ui.material')}</th>
                          <th>{tCommon('ui.description')}</th>
                          <th>{t('receivingOperations.columns.ordered')}</th>
                          <th>{t('receivingOperations.columns.received')}</th>
                          <th>{t('receivingOperations.columns.batchNumber')}</th>
                          <th>{t('receivingOperations.columns.location')}</th>
                          <th>{t('receivingOperations.columns.quality')}</th>
                          <th>{tCommon('ui.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grnDetails.items && grnDetails.items.length > 0 ? (
                          grnDetails.items.map(item => {
                            let qualityClass = 'status-success';
                            if (item.quality_status === 'QUARANTINE') qualityClass = 'status-warning';
                            if (item.quality_status === 'FAILED') qualityClass = 'status-error';

                            return (
                              <tr key={item.grn_item_id}>
                                <td><strong>{item.item_code}</strong></td>
                                <td>{item.item_name}</td>
                                <td>{parseFloat(item.quantity_ordered || 0).toLocaleString()} {item.unit_of_measure}</td>
                                <td><strong>{parseFloat(item.quantity_received).toLocaleString()}</strong> {item.unit_of_measure}</td>
                                <td>{item.batch_number || '-'}</td>
                                <td>{item.location || '-'}</td>
                                <td>
                                  <span className={`status-badge ${qualityClass}`}>{statusLabel(item.quality_status)}</span>
                                  {item.damage_notes && (
                                    <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                      {item.damage_notes.length > 50 ? item.damage_notes.substring(0, 50) + '...' : item.damage_notes}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {grnDetails.status !== 'CANCELLED' && (item.quality_status === 'QUARANTINE' || item.quality_status === 'FAILED') && (
                                    <>
                                      <button
                                        className="btn-action btn-primary"
                                        onClick={() => handleInspectFromGRN(item)}
                                        style={{ fontSize: '0.85em', padding: '0.4rem 0.6rem' }}
                                      >
                                        {t('receivingOperations.buttons.inspect')}
                                      </button>
                                      {item.quality_status === 'FAILED' && (
                                        <button
                                          className="btn-action btn-danger"
                                          onClick={() => handleCreateReturn(item)}
                                          style={{ fontSize: '0.85em', padding: '0.4rem 0.6rem', marginLeft: '0.5rem' }}
                                        >
                                          {t('receivingOperations.buttons.return')}
                                        </button>
                                      )}
                                    </>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                              {t('receivingOperations.empty.noGrnItems')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  {canCancelGRN && grnDetails.status !== 'CANCELLED' && (
                    <button className="btn-action btn-danger" onClick={() => setShowCancelGRNModal(true)}>
                      {t('receivingOperations.buttons.cancelGrn')}
                    </button>
                  )}
                  <button className="btn-action btn-secondary" onClick={closeGRNDetailsModal}>{tCommon('ui.close')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel GRN Modal (#281) */}
      {showCancelGRNModal && grnDetails && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => !cancellingGRN && closeCancelGRNModal()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{t('receivingOperations.cancelModal.title', { grnNumber: grnDetails.grn_number })}</h2>
              <button className="modal-close" onClick={closeCancelGRNModal} disabled={cancellingGRN}>&#x2715;</button>
            </div>
            <div className="modal-body">
              <p>{t('receivingOperations.cancelModal.intro')}</p>
              <ul>
                <li>{t('receivingOperations.cancelModal.stockReversed')}</li>
                <li>{t('receivingOperations.cancelModal.poPending', { poNumber: grnDetails.po_number })}</li>
              </ul>
              <p>{t('receivingOperations.cancelModal.rejectedIfUsed')}</p>
              <label htmlFor="grn-cancel-reason">{t('receivingOperations.cancelModal.reasonLabel')}</label>
              <textarea
                id="grn-cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('receivingOperations.cancelModal.reasonPlaceholder')}
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeCancelGRNModal} disabled={cancellingGRN}>
                {tCommon('ui.back')}
              </button>
              <button
                className="btn-action btn-danger"
                onClick={handleCancelGRN}
                disabled={cancellingGRN || cancelReason.trim().length < 5}
              >
                {cancellingGRN ? t('receivingOperations.buttons.cancelling') : t('receivingOperations.buttons.confirmCancelGrn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

ReceivingOperations.displayName = 'ReceivingOperations';

export default ReceivingOperations;
